import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LendioAngularMaterialThemeService {

  private enabledSubject: BehaviorSubject<boolean>;
  private enabledObservable: Observable<boolean>;
  private enabledRoutePatterns = [
    /^\/auth\/login/,
    /^\/auth\/reset-password/,
    /^\/auth\/account-locked/,
  ];

  private state = {
    enabledByRoute: false,
    enabledByUser: false,
  };

  constructor(router: Router) {
    this.enabledSubject = new BehaviorSubject(false);
    this.enabledObservable = this.enabledSubject.asObservable();
    const enabled = localStorage.getItem('lendio-angular-material-theme') === 'true';
    this.setEnabledByUser(enabled);
    this.enableBasedOnRoute(router);
  }

  /**
   * Emits true whether theme enabled by any means (user or route).
   *
   * Use this to subscribe respond to theme enabling events.
   */
  get enabled(): Observable<boolean> {
    return this.enabledObservable;
  }

  /**
   * Sets enabledByUser to true.
   */
  enable(): void {
    this.setEnabledByUser(true);
  }

  /**
   * Sets enabledByUser to false.
   */
  disable(): void {
    this.setEnabledByUser(false);
  }

  forceDisable(): void {
    this.publish({ value: false });
  }

  /**
   * Toggles enabledByUser.
   */
  toggle(): void {
    this.setEnabledByUser(!this.state.enabledByUser);
  }

  private setEnabledByUser(value: boolean) {
    this.state.enabledByUser = value;
    localStorage.setItem('lendio-angular-material-theme', `${value}`);
    this.publish();
  }

  private setEnabledByRoute(value: boolean) {
    this.state.enabledByRoute = value;
    this.publish();
  }

  private publish(override?: { value: boolean }) {
    const enabled = override ? override.value : this.state.enabledByUser || this.state.enabledByRoute;
    this.updateStylesheet(enabled);
    this.updateThemeClass(enabled);
    this.enabledSubject.next(enabled);
  }

  private updateStylesheet(enabled: boolean) {
    if (enabled) {
      document.getElementById('lendio-material-angular-theme')?.removeAttribute('disabled');
    } else {
      document.getElementById('lendio-material-angular-theme')?.setAttribute('disabled', '');
    }
  }

  private updateThemeClass(enabled: boolean) {
    const themeClasses: { [key: string]: boolean } = {
      'lendio-angular-material-theme-v1': enabled,
      'lendio-angular-material-theme-v0': !enabled,
    };
    Object.keys(themeClasses).forEach((themeClass: string) => {
      const themeClassEnabled = themeClasses[themeClass];
      if (themeClassEnabled) {
        if (!document.body.classList.contains(themeClass)) {
          document.body.classList.add(themeClass);
        }
      } else {
        if (document.body.classList.contains(themeClass)) {
          document.body.classList.remove(themeClass);
        }
      }
    });
  }

  private enableBasedOnRoute(router: Router) {
    router.events.forEach((event) => {
      if (!(event instanceof NavigationEnd)) {
        return;
      }
      const enabled = this.enabledRoutePatterns.some(pattern => {
        return pattern.test(event.urlAfterRedirects);
      });
      this.setEnabledByRoute(enabled);
    });
  }
}
